import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapPinPlus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { generateMapLibreStaticImage } from "@/lib/mapbox";
import { useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MapCard } from "@/components/map-card";
import { EmptyState } from "@/components/empty-state";
import { ErrorState } from "@/components/error-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  getListWorkspacesQueryKey,
  useDeleteWorkspace,
  useListWorkspaces,
} from "@/api/orval";

export const Home = () => {
  const {
    data: workspaces,
    isLoading: isWorkspacesLoading,
    isError: isWorkspacesError,
  } = useListWorkspaces();
  const deleteWorkspace = useDeleteWorkspace();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { setOpen } = useSidebar();
  const navigate = useNavigate();
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [cityToDelete, setCityToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const handleNewCity = () => navigate("/onboarding");

  const handleDeleteCity = async () => {
    if (!cityToDelete) return;
    try {
      await deleteWorkspace.mutateAsync({
        pathParams: { workspaceId: cityToDelete.id },
      });
      await queryClient.invalidateQueries({
        queryKey: getListWorkspacesQueryKey(),
      });
      toast({
        title: "City deleted",
        description: `${cityToDelete.name} and its runs were removed.`,
      });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Couldn't delete that city",
        description: err?.response?.data?.detail ?? "Please try again.",
      });
    } finally {
      setCityToDelete(null);
    }
  };

  useEffect(() => {
    const generateImages = async () => {
      if (!workspaces) return;

      const urls: Record<string, string> = {};

      for (const workspace of workspaces) {
        if (workspace.latitude && workspace.longitude) {
          urls[workspace.id] = await generateMapLibreStaticImage(
            workspace.latitude,
            workspace.longitude,
          );
        }
      }

      setImageUrls(urls);
    };

    generateImages();
  }, [workspaces]);

  const handleWorkspaceClick = (slug: string) => {
    navigate(`/city/${slug}`);
    setOpen(false);
  };

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Cities</h2>
        {workspaces && workspaces.length > 0 && (
          <div className="flex items-center gap-4">
            <Button onClick={handleNewCity}>New city</Button>
          </div>
        )}
      </div>

      {isWorkspacesError ? (
        <ErrorState
          title="Couldn't load your cities"
          description="Something went wrong. Try reloading the page."
          action={
            <Button onClick={() => window.location.reload()}>Reload</Button>
          }
        />
      ) : isWorkspacesLoading ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3] w-full rounded-xl" />
          ))}
        </div>
      ) : workspaces && workspaces.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {workspaces.map((workspace) => (
            <MapCard
              key={workspace.id}
              title={workspace.name}
              imageUrl={imageUrls[workspace.id]}
              onClick={() => handleWorkspaceClick(workspace.slug)}
              onDelete={() =>
                setCityToDelete({ id: workspace.id, name: workspace.name })
              }
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<MapPinPlus className="h-10 w-10" />}
          title="No cities yet"
          description="Add a city to start tracking which of its streets you've run."
          action={<Button onClick={handleNewCity}>Add your first city</Button>}
        />
      )}

      <AlertDialog
        open={!!cityToDelete}
        onOpenChange={(open) => !open && setCityToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {cityToDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the city along with its street coverage
              and imported runs. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteWorkspace.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteCity();
              }}
              disabled={deleteWorkspace.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteWorkspace.isPending ? "Deleting..." : "Delete city"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
